import { useState } from 'react';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { senhaForte } from '@/lib/senha_forte';
import { definirAcesso } from '@/lib/plataforma_admin';
import { definirSenhaProvisoria, excluirPessoa } from '@/lib/plataforma_pessoa';
import { Button, Input } from '@/components/ui';
import { Secao, type SecaoProps } from './PessoaSecoes';

/** Senha provisória pelo administrador: a troca é obrigatória no próximo acesso. */
export function SenhaSecao({ pessoa, salvando, executar }: SecaoProps) {
  const apiClient = useApiClient();
  const [senha, setSenha] = useState('');
  const [recusa, setRecusa] = useState<string | null>(null);

  async function definir() {
    if (!senhaForte(senha)) {
      setRecusa(ptBR.admin.validationSenhaCurta);
      return;
    }
    setRecusa(null);
    const feita = await executar(
      () => definirSenhaProvisoria(apiClient, pessoa.id, senha),
      ptBR.admin.pessoaSenhaSucesso,
    );
    if (feita) setSenha('');
  }

  return (
    <Secao titulo={ptBR.admin.pessoaSecaoSenha}>
      <p className="text-body-sm text-fg-muted">{ptBR.admin.pessoaSenhaHint}</p>
      {pessoa.senha_provisoria_pendente ? (
        <p className="text-body-sm text-fg-muted" data-testid="pessoa-senha-pendente">
          {ptBR.admin.pessoaSenhaPendente}
        </p>
      ) : null}
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex min-w-[220px] flex-col gap-1 text-body-sm text-fg-muted">
          <span>{ptBR.admin.pessoaSenhaNova}</span>
          <Input
            type="password"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            data-testid="pessoa-senha"
          />
        </label>
        <Button
          variant="secondary"
          size="sm"
          type="button"
          disabled={salvando || senha === ''}
          onClick={() => void definir()}
          data-testid="pessoa-senha-definir"
        >
          {ptBR.admin.pessoaSenhaSubmit}
        </Button>
      </div>
      <p className="text-caption text-fg-dim">{ptBR.senhaForte.regra}</p>
      {recusa !== null ? (
        <p role="alert" className="text-body-sm text-danger-700" data-testid="pessoa-senha-recusa">
          {recusa}
        </p>
      ) : null}
    </Secao>
  );
}

interface ConfirmacaoProps {
  pergunta: string;
  salvando: boolean;
  testid: string;
  onSim(): void;
  onNao(): void;
}

function Confirmacao({ pergunta, salvando, testid, onSim, onNao }: ConfirmacaoProps) {
  return (
    <span className="flex flex-wrap items-center gap-1.5" data-testid={`confirmar-${testid}`}>
      <span className="text-body-sm text-fg-muted">{pergunta}</span>
      <Button
        variant="secondary"
        size="sm"
        type="button"
        disabled={salvando}
        onClick={onSim}
        data-testid={`${testid}-confirmar`}
      >
        {ptBR.admin.confirmarSim}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        type="button"
        onClick={onNao}
        data-testid={`${testid}-cancelar`}
      >
        {ptBR.admin.confirmarNao}
      </Button>
    </span>
  );
}

/** Desativar e reativar; excluir só quem ainda não usou o sistema (ADR-0127 §1). */
export function AcessoSecao({ pessoa, salvando, executar }: SecaoProps) {
  const apiClient = useApiClient();
  const [confirmando, setConfirmando] = useState<'acesso' | 'excluir' | null>(null);
  const testidAcesso = `acesso-${pessoa.id}`;

  return (
    <Secao titulo={ptBR.admin.pessoaSecaoAcesso}>
      <div className="flex flex-wrap items-center gap-2">
        {confirmando === 'acesso' ? (
          <Confirmacao
            pergunta={ptBR.admin.confirmarPergunta}
            salvando={salvando}
            testid={testidAcesso}
            onSim={() => {
              setConfirmando(null);
              void executar(
                () => definirAcesso(apiClient, pessoa.id, !pessoa.ativo),
                ptBR.admin.acaoSucesso,
              );
            }}
            onNao={() => setConfirmando(null)}
          />
        ) : (
          <Button
            variant="secondary"
            size="sm"
            type="button"
            disabled={salvando}
            onClick={() => setConfirmando('acesso')}
            data-testid={testidAcesso}
          >
            {pessoa.ativo ? ptBR.admin.desativarAcesso : ptBR.admin.reativarAcesso}
          </Button>
        )}
        {pessoa.tem_rastro ? null : confirmando === 'excluir' ? (
          <Confirmacao
            pergunta={ptBR.admin.pessoaExcluirConfirmar}
            salvando={salvando}
            testid="pessoa-excluir"
            onSim={() => {
              setConfirmando(null);
              void executar(
                () => excluirPessoa(apiClient, pessoa.id),
                ptBR.admin.pessoaExcluirSucesso,
                {
                  fechar: true,
                },
              );
            }}
            onNao={() => setConfirmando(null)}
          />
        ) : (
          <Button
            variant="secondary"
            size="sm"
            type="button"
            disabled={salvando}
            onClick={() => setConfirmando('excluir')}
            data-testid="pessoa-excluir"
          >
            {ptBR.admin.pessoaExcluir}
          </Button>
        )}
      </div>
      <p className="text-body-sm text-fg-muted" data-testid="pessoa-excluir-situacao">
        {pessoa.tem_rastro ? ptBR.admin.pessoaExcluirBloqueado : ptBR.admin.pessoaExcluirHint}
      </p>
    </Secao>
  );
}
